import Button from "../components/ds/Button";
import EmptyState from "../components/ds/EmptyState";
import Icon from "../components/ds/Icon";
import Page from "../components/ds/Page";
import PageHeader from "../components/ds/PageHeader";
import TextField from "../components/ds/TextField";
import { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  startTopUpCheckout,
  startCampaignFundCheckout,
} from "../api/checkout";
import CheckoutModal from "../components/CheckoutModal";
import { fundCampaignFromBalance } from "../api/campaigns";
import { getBalance } from "../api/balance";
import { useAuth } from "../context/AuthContext";
import { useTranslation } from "../context/TranslationContext";
import { useSocket } from "../context/SocketContext";

const PaymentPage = ({ isDark, setToast }) => {
  const { t } = useTranslation();
  // t() returns the key itself when a string is missing, so `|| fallback` never fires.
  const tr = (key, fallback, params) => {
    const text = t(key, params);
    return text && text !== key ? text : fallback;
  };
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { socket } = useSocket();
  const { user } = useAuth();
  // Get campaign info from URL params
  const campaignId = searchParams.get("campaign_id");
  const campaignName = searchParams.get("campaign_name");

  // Card form state
  const [amount, setAmount] = useState("");
  const [keepzLoading, setKeepzLoading] = useState(false);
  /** Set once an order exists; the modal renders Keepz's page in an iframe. */
  const [checkoutOrder, setCheckoutOrder] = useState(null);

  // UI state
  const [fundFromBalanceLoading, setFundFromBalanceLoading] = useState(false);
  const [walletAvailable, setWalletAvailable] = useState(null);
  const [errors, setErrors] = useState({});
  const [paymentStatus, setPaymentStatus] = useState(null); // null, 'success', 'error'
  const [paymentMessage, setPaymentMessage] = useState("");


  /** The signed-in user's available balance, used for funding a campaign from it. */
  const loadWallet = useCallback(async () => {
    const uid = user?.id ?? user?._id;
    if (!uid) {
      setWalletAvailable(null);
      return;
    }
    try {
      const b = await getBalance(uid);
      setWalletAvailable(Number(b?.amount) || 0);
    } catch {
      setWalletAvailable(0);
    }
  }, [user?.id, user?._id]);

  useEffect(() => {
    loadWallet();
  }, [loadWallet]);

  useEffect(() => {
    const onBal = () => loadWallet();
    window.addEventListener("balance-updated", onBal);
    return () => window.removeEventListener("balance-updated", onBal);
  }, [loadWallet]);

  const handleFundFromBalance = async () => {
    if (!campaignId) return;
    const amt = parseFloat(String(amount).replace(",", "."));
    if (!Number.isFinite(amt) || amt <= 0) {
      if (setToast) setToast(t("toasts.invalidAmount"));
      return;
    }
    const avail = walletAvailable ?? 0;
    if (amt > avail + 1e-9) {
      if (setToast)
        setToast(
          t("campaigns.insufficientWallet") ||
            `Insufficient wallet balance. Available: ${avail.toFixed(2)} ₾`
        );
      return;
    }
    setFundFromBalanceLoading(true);
    try {
      const res = await fundCampaignFromBalance(campaignId, amt);
      if (res.success) {
        setPaymentStatus("success");
        setPaymentMessage(t("campaigns.fundedFromWalletMessage", { amount: amt.toFixed(2) }));
        if (typeof res.balance?.amount === "number") {
          setWalletAvailable(res.balance.amount);
        } else {
          await loadWallet();
        }
        window.dispatchEvent(new CustomEvent("balance-updated"));
        if (setToast) setToast(t("toasts.paymentSuccessful") || "Success");
      }
    } catch (error) {
      const msg =
        error.response?.data?.message ||
        error.message ||
        t("toasts.paymentFailedTryAgain");
      if (setToast) setToast(msg);
    } finally {
      setFundFromBalanceLoading(false);
    }
  };

  /**
   * Pay with the method chosen above. Card details are entered on the provider's page,
   * so nothing sensitive touches this form or our servers.
   */
  const handleKeepzCheckout = async () => {
    const amt = parseFloat(String(amount).replace(",", "."));
    if (!amt || amt <= 0) {
      setErrors((prev) => ({ ...prev, amount: "Please enter a valid amount" }));
      return;
    }
    setKeepzLoading(true);
    try {
      const result = campaignId
        ? await startCampaignFundCheckout(campaignId, amt)
        : await startTopUpCheckout(amt);

      if (!result?.checkoutUrl) throw new Error("No checkout URL was returned");

      // Keepz's page has a fixed minimum width and clips rather than reflowing, so
      // there is no honest way to frame it on a phone — and a bank's open-banking
      // login refuses to be framed at all. @see components/CheckoutModal
      if (window.innerWidth < 760 || result.frameable === false) {
        window.location.href = result.checkoutUrl;
        return;
      }
      setCheckoutOrder(result);
      setKeepzLoading(false);
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.message ||
        "Could not start checkout. Please try again.";
      if (setToast) setToast(msg);
      setKeepzLoading(false);
    }
  };

  // Render success state
  if (paymentStatus === "success") {
    return (
      <Page>
        <div className="rounded-radius-lg bg-bg-raised">
          <EmptyState
            icon="check"
            title={campaignName ? "Campaign Funded!" : "Payment Successful!"}
            message={paymentMessage || (campaignName ? `Your campaign "${campaignName}" has been funded successfully.` : "")}
            actionLabel={campaignId ? "Back to Campaign" : "Return to Home"}
            onAction={() => (campaignId ? navigate(`/campaigns/${campaignId}`) : navigate("/"))}
          />
        </div>
      </Page>
    );
  }

  // Render error state
  if (paymentStatus === "error") {
    return (
      <Page>
        <div className="rounded-radius-lg bg-bg-raised">
          <EmptyState
            icon="warning"
            title="Payment Failed"
            message={paymentMessage}
            actionLabel="Try Again"
            onAction={() => {
              setPaymentStatus(null);
              setPaymentMessage("");
            }}
          />
        </div>
      </Page>
    );
  }

  return (
    <Page>
      <PageHeader
        eyebrow={campaignName ? "Campaign Top-up" : "Payment"}
        title={campaignName ? `Top-up: ${campaignName}` : "Complete Your Payment"}
        subtitle={
          campaignName ? "Add funds to your campaign budget" : tr("payment.topUpSubtitle", "Choose an amount and how you'd like to pay")
        }
        action={
          <span className="inline-flex items-center gap-space-8 text-footnote-semibold text-label-secondary">
            <Icon name="lock" size={16} />
            Secure Payment
          </span>
        }
      />

      <section className="flex flex-col gap-space-16 rounded-radius-lg bg-bg-raised p-space-16 min-[600px]:p-space-24">
        <TextField
          label="Amount (GEL)"
          type="number"
          step="0.01"
          min="0"
          value={amount}
          onChange={(e) => {
            setAmount(e.target.value);
            if (errors.amount) setErrors((prev) => ({ ...prev, amount: null }));
          }}
          placeholder="0.00"
          icon={<span className="text-subheadline text-label-secondary">₾</span>}
          error={errors.amount}
        />

        {campaignId && (
          <div className="flex items-start gap-space-12 rounded-[22px] bg-bg-inset p-space-16">
            <span className="flex size-9 shrink-0 items-center justify-center rounded-[12px] bg-fill-tertiary text-label-primary">
              <Icon name="wallet" size={18} />
            </span>
            <div className="flex min-w-0 flex-1 flex-col gap-space-4">
              <p className="text-subheadline-semibold text-label-primary">{t("campaigns.fundFromWallet")}</p>
              <p className="text-footnote text-label-secondary">{t("campaigns.fundFromWalletHint")}</p>
              {walletAvailable != null && (
                <p className="text-footnote-semibold text-label-primary">
                  {t("campaigns.walletAvailable")}: {walletAvailable.toFixed(2)} ₾
                </p>
              )}
              <Button size="m" variant="tinted" className="mt-space-8 self-start" loading={fundFromBalanceLoading} onClick={handleFundFromBalance}>
                {fundFromBalanceLoading ? t("campaigns.transferringFromBalance") : t("campaigns.transferToCampaign")}
              </Button>
            </div>
          </div>
        )}

        {campaignId && (
          <p className="text-center text-caps-label uppercase text-label-tertiary">{tr("payment.orPayDirectly", "Or pay directly")}</p>
        )}

        {/* Keepz hosted checkout — card details are entered on their page. */}
        <Button fullWidth icon={<Icon name="lock" size={20} />} loading={keepzLoading} onClick={handleKeepzCheckout}>
          {keepzLoading
            ? tr("payment.starting", "Starting payment…")
            : amount && parseFloat(amount) > 0
            ? tr("payment.payAmount", `Pay ₾${parseFloat(amount).toFixed(2)}`, { amount: `₾${parseFloat(amount).toFixed(2)}` })
            : tr("payment.pay", "Pay")}
        </Button>
      </section>

      <CheckoutModal
        open={Boolean(checkoutOrder)}
        orderId={checkoutOrder?.orderId}
        checkoutUrl={checkoutOrder?.checkoutUrl}
        amount={checkoutOrder?.amount}
        currency={checkoutOrder?.currency}
        isDark={isDark}
        onClose={() => setCheckoutOrder(null)}
        onPaid={() => {
          setCheckoutOrder(null);
          setPaymentStatus("success");
          setPaymentMessage(
            campaignName
              ? t("campaigns.fundedMessage") || "Campaign funded."
              : "Your balance has been topped up."
          );
          // The balance page and header listen for this.
          window.dispatchEvent(new CustomEvent("balance-updated"));
        }}
      />
    </Page>
  );
};

export default PaymentPage;
